<script lang="ts">
/**
 * The hero lede with one moving part: "AI agents for ___." cycles through the desks the crew works at
 * and rests a little longer on "every desk on the floor." The rest of the lede stays still. The first
 * sentence leads, larger and in ink, with the desk highlighted on its own line. Every option ends in
 * its own period, so a short desk name never leaves a gap before the stop and nothing below reflows.
 *
 * It renders on its resting desk, "every desk on the floor.", from the first paint, so the line is the
 * same height before and after script runs and nothing below it shifts. With reduced motion it stays
 * there. Screen readers get one plain sentence.
 */
import { onMount } from "svelte";

const DESKS = ["purchasing.", "quality.", "planning.", "engineering.", "sales.", "maintenance.", "every desk on the floor."];
const STILL = "every desk on the floor, from purchasing to quality.";
/** How long each desk shows, and how long the line rests on the last one. */
const STEP = 1600;
const REST = 3600;

let i = $state(DESKS.length - 1);

onMount(() => {
	if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
	let timer: ReturnType<typeof setTimeout>;
	const next = () => {
		i = (i + 1) % DESKS.length;
		timer = setTimeout(next, i === DESKS.length - 1 ? REST : STEP);
	};
	timer = setTimeout(next, 900);
	return () => clearTimeout(timer);
});
</script>

<span class="first">
	AI agents for
	<span class="slot" aria-live="off">
		<span class="sr-only">{STILL}</span>
		{#each DESKS as d, n (d)}
			<b class="desk" class:on={n === i} class:gone={n === (i + DESKS.length - 1) % DESKS.length} aria-hidden="true">{d}</b>
		{/each}
	</span>
</span>
Pre6 agents work together 24/7, learning from decades of your organizational memory. Your experts just review.

<style>
	/* The first line leads the lede: bigger and in ink, with the desk marked like the page's other highlights. */
	.first {
		display: block;
		margin-bottom: 10px;
		color: var(--ink);
		font-size: clamp(20px, 1.7vw, 24px);
		font-weight: 600;
		line-height: 1.35;
		letter-spacing: -0.01em;
	}

	b {
		padding: 0 5px;
		border-radius: 3px;
		background: var(--blue-100);
		color: var(--blue-800);
		font-weight: 700;
		-webkit-box-decoration-break: clone;
		box-decoration-break: clone;
	}

	/* Every desk stacked in one cell; only the current one shows, rising in as the last one lifts out. */
	/* The desk always sits on its own line under "AI agents for", so the width never jumps between lines. */
	.slot {
		position: relative;
		display: grid;
		justify-items: start;
		margin-top: 2px;
		overflow: hidden;
		white-space: nowrap;
	}

	.desk {
		grid-area: 1 / 1;
		justify-self: start;
		opacity: 0;
		translate: 0 70%;
	}

	.desk.on {
		opacity: 1;
		translate: 0 0;
		transition:
			opacity 0.35s ease-out,
			translate 0.55s cubic-bezier(0.34, 1.45, 0.64, 1);
	}

	.desk.gone {
		opacity: 0;
		translate: 0 -70%;
		transition:
			opacity 0.3s ease-in,
			translate 0.35s ease-in;
	}

	.sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		overflow: hidden;
		clip-path: inset(50%);
		white-space: nowrap;
	}
</style>
